import { computed } from '@vue/composition-api';
import { QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
/**
 * Parses the answer value of a branching question into individual selected values.
 * Mirrors parseBranchingAnswerValues() from api/src/modules/surveys/services/surveys.service.ts
 */
function parseBranchingAnswerValues(type, raw) {
    const trimmed = (raw ?? '').trim();
    if (!trimmed)
        return [];
    if (type === QuestionType.CHECKBOX) {
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed
                    .filter((v) => typeof v === 'string')
                    .map((v) => v.trim())
                    .filter(Boolean);
            }
        }
        catch {
            // ignore
        }
        if (trimmed.includes(SURVEY_ANSWER_SEPARATOR)) {
            return trimmed
                .split(SURVEY_ANSWER_SEPARATOR)
                .map((v) => v.trim())
                .filter(Boolean);
        }
        return [trimmed];
    }
    return [trimmed];
}
/**
 * Given questions and current answers, compute which question indices are visible.
 * Mirrors getVisibleQuestions() from api/src/modules/surveys/services/surveys.service.ts
 */
export function getVisibleQuestionIndices(questions, answers) {
    const branchingIdx = questions.findIndex((q) => Array.isArray(q.branchingRules) && q.branchingRules.length > 0);
    if (branchingIdx === -1) {
        return new Set(questions.map((_, idx) => idx));
    }
    const branchingQuestion = questions[branchingIdx];
    const rules = branchingQuestion.branchingRules ?? [];
    const alwaysVisible = new Set();
    for (let i = 0; i <= branchingIdx; i++) {
        alwaysVisible.add(i);
    }
    const referenced = new Set();
    for (const rule of rules) {
        for (const idx of rule.showQuestions ?? []) {
            referenced.add(idx);
        }
    }
    for (let i = branchingIdx + 1; i < questions.length; i++) {
        if (!referenced.has(i)) {
            alwaysVisible.add(i);
        }
    }
    const visible = new Set(alwaysVisible);
    const rawBranchAnswer = answers[branchingIdx] ?? '';
    const selected = parseBranchingAnswerValues(branchingQuestion.type, rawBranchAnswer);
    if (selected.length === 0) {
        return visible;
    }
    for (const rule of rules) {
        if (selected.includes(rule.answerValue)) {
            for (const idx of rule.showQuestions ?? []) {
                visible.add(idx);
            }
        }
    }
    return visible;
}
/**
 * Composable for reactive survey branching logic.
 */
export function useSurveyBranching(questions, getAnswerForIndex) {
    const visibleQuestionIndices = computed(() => {
        const qs = questions.value;
        const answers = qs.map((_, idx) => getAnswerForIndex(idx));
        return getVisibleQuestionIndices(qs, answers);
    });
    const isQuestionVisible = (idx) => visibleQuestionIndices.value.has(idx);
    return {
        visibleQuestionIndices,
        isQuestionVisible,
    };
}
